// Helpers for linked payments. A payment is ONE record; the bills and
// subscriptions it belongs to reference it, and it shows up on each of them.
// Linking adds a reference, unlinking removes one, and the payment itself
// survives for as long as any parent still references it — it is deleted only
// once nothing does.
//
// A payment carries a `links` ARRAY (v2). Payments written by v1 instead carry
// five scalar fields describing their single link, and the backend still echoes
// those scalars derived from links[0], so both shapes are read here.
//
// The two payment endpoints disagree on key casing:
//   GET /bills/{id}/payments          → Title Cased  ("Links", "Linked Parent Name")
//   GET /subscriptions/{id}/payments  → snake_case   ("links", "linked_parent_name")
// Only the TOP-LEVEL keys are Title-Cased; the dicts inside the array keep their
// snake_case keys either way.

// Every casing the link fields can arrive in. Used to keep them out of the
// dynamically-derived column list in the bill payments table, and to strip them
// from an edit payload.
export const LINK_FIELD_KEYS = [
  'links',
  'link_id',
  'linked_type',
  'linked_parent_id',
  'linked_payment_id',
  'linked_parent_name',
  'Links',
  'Link Id',
  'Linked Type',
  'Linked Parent Id',
  'Linked Payment Id',
  'Linked Parent Name',
];

const normaliseLink = (entry) => {
  if (!entry || !entry.link_id) return null;
  return {
    link_id: entry.link_id,
    role: entry.role || '',
    linked_type: entry.linked_type || '',
    linked_parent_id: entry.linked_parent_id || '',
    linked_payment_id: entry.linked_payment_id || '',
    linked_parent_name: entry.linked_parent_name || '',
  };
};

// Returns null when the payment carries no v1 scalar link. Kept as the legacy
// fallback for payments that predate the `links` array.
export const readPaymentLink = (payment) => {
  if (!payment) return null;
  const pick = (snake, title) => payment[snake] ?? payment[title];

  return normaliseLink({
    link_id: pick('link_id', 'Link Id'),
    // v1 pairs behave like two mirrors of each other: deleting either copy
    // removes both. That is what role "mirror" means in v2.
    role: 'mirror',
    linked_type: pick('linked_type', 'Linked Type'),
    linked_parent_id: pick('linked_parent_id', 'Linked Parent Id'),
    linked_payment_id: pick('linked_payment_id', 'Linked Payment Id'),
    linked_parent_name: pick('linked_parent_name', 'Linked Parent Name'),
  });
};

// The one entry point for rendering: always an array, empty when the payment is
// not linked. Unlinked payments simply have no link fields at all, so "missing"
// is the normal case, not an error.
export const readPaymentLinks = (payment) => {
  if (!payment) return [];

  const raw = payment.links ?? payment.Links;
  if (Array.isArray(raw)) {
    return raw.map(normaliseLink).filter(Boolean);
  }

  const legacy = readPaymentLink(payment);
  return legacy ? [legacy] : [];
};

// The parent ids on the other end, used to stop the picker offering a
// counterpart this payment is already linked to (the backend answers 409).
export const linkedParentIds = (payment) =>
  readPaymentLinks(payment)
    .map((link) => link.linked_parent_id)
    .filter(Boolean);

// The other side's display name, with a fallback for every way it can be
// missing — a chip must never read "Linked to undefined".
export const linkedParentLabel = (link) => {
  if (!link) return '';
  if (link.linked_parent_name) return link.linked_parent_name;
  if (link.linked_type === 'bill') return 'a bill';
  if (link.linked_type === 'subscription') return 'a subscription';
  return 'another record';
};

// One chip per link, so the chip carries just the counterpart's name; the
// full sentence goes in its tooltip.
export const linkChipText = (link) => (link ? `🔗 ${linkedParentLabel(link)}` : '');

// The tooltip is the only place the model is ever explained, so it says the
// thing that matters: it is the same payment on both sides, not a copy.
export const linkChipTitle = (link) =>
  link ? `Also on ${linkedParentLabel(link)} — one payment, shown on both.` : '';

// The words the UI uses for linking, unlinking and deleting. They live here,
// next to the data they describe, because the app used to ask "Link this
// payment to a Subscription?" and never once say what that did to the payment.

export const linkPromptTitle = (targetLabel = 'record') =>
  `Also link this payment to a ${targetLabel}?`;

export const LINK_PROMPT_HINT =
  'It stays one payment, shown on both records. Nothing is copied.';

export const unlinkButtonTitle = (link) =>
  link ? `Unlink from ${linkedParentLabel(link)}. The payment stays here.` : '';

export const unlinkConfirmText = (link) =>
  link ? `Unlink from ${linkedParentLabel(link)}? The payment stays here.` : '';

export const LINKED_NOTICE = 'Linked. It is the same payment on both records.';
export const UNLINKED_NOTICE = 'Unlinked. The payment is still here.';

// "a", "a and b", "a, b and c" — a warning that names the other parents has to
// read like a sentence, not like a serialised array.
const listNames = (names) => {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

// What a delete from ONE parent actually does. Only when nothing else
// references the payment does removing it here end the payment itself, and the
// links we already hold are what tells the two cases apart.
export const deletePaymentWarning = (payment, hereLabel = 'this record') => {
  const others = readPaymentLinks(payment).map(linkedParentLabel).filter(Boolean);
  if (others.length === 0) {
    return 'Delete this payment? Nothing else is linked to it, so it will be gone for good.';
  }
  return `Remove this payment from ${hereLabel}? It is still linked to ${listNames(
    others
  )}, so the payment itself is kept.`;
};

// The aggregate Payments page deletes the payment itself rather than one
// parent's reference to it, so the warning has to be the blunter one.
export const deletePaymentEverywhereWarning = (payment) => {
  const others = readPaymentLinks(payment).map(linkedParentLabel).filter(Boolean);
  if (others.length === 0) {
    return 'Delete this payment? It will be gone for good.';
  }
  return `Delete this payment? It is linked to ${listNames(
    others
  )} — it will be removed from there too.`;
};
